import type { ReactNode } from 'react';
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
} from 'react-router-dom';

import { AppLayout } from '@/components/layout/AppLayout';
import { AssignmentListPage } from '@/features/assignments/AssignmentListPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { AssignmentDisciplinePage } from '@/features/discipline/AssignmentDisciplinePage';
import { StudentListPage } from '@/features/students/StudentListPage';
import { useAuthStore } from '@/stores/authStore';

function ProtectedRoute({ children }: { children?: ReactNode }) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: { pathname: location.pathname } }}
      />
    );
  }

  return children ? <>{children}</> : <Outlet />;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/students" replace />,
      },
      {
        path: 'students',
        element: <StudentListPage />,
      },
      {
        path: 'assignments',
        element: <AssignmentListPage />,
      },
      {
        path: 'discipline/assignments',
        element: <AssignmentDisciplinePage />,
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

// NB: the v7_startTransition future flag lives on <RouterProvider future={...}>
// in react-router-dom 6.30, not on createBrowserRouter options. See App.tsx.
